/**
 * Supplier Autocomplete Hook
 * 
 * Provides autocomplete functionality for supplier search using Elasticsearch.
 * Supports debounced search with minimum 2 characters and popular items on empty query.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import type { APISupplierAutocompleteResponse } from '../../../generated-types/models/APISupplierAutocompleteResponse'
import type { V1SearchSuppliersSuppliersAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

interface SupplierAutocompleteResponse {
  suggestions: Array<{
    id: string
    name: string
    self: string
  }>
  total: number
}

/**
 * Fetches supplier autocomplete suggestions
 */
const fetchSupplierAutocomplete = async (
  query: string,
  size = 10
): Promise<SupplierAutocompleteResponse> => {
  const response: APISupplierAutocompleteResponse = await v1Api.v1SearchSuppliersSuppliersAutocompleteRetrieve({
    q: query || undefined,
    size,
  } as V1SearchSuppliersSuppliersAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
      self: `${baseUrl}v1/supplier/suppliers/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for supplier autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 */
export function useSupplierAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<SupplierAutocompleteResponse, Error>({
    queryKey: ['supplier-autocomplete', debouncedQuery],
    queryFn: () => fetchSupplierAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


